export type TimelineBreakdown = 'total' | 'tagger' | 'class' | 'workingState';
export type TimelineMetric = 'accumulated' | 'slides' | 'annotations' | 'changes';
export type TimelineView = 'workflow' | 'slideActivity' | 'annotationContent' | 'revisions';

export interface TimelineViewConfig {
  label: string;
  description: string;
  metric: TimelineMetric;
  defaultBreakdown: TimelineBreakdown;
  breakdowns: TimelineBreakdown[];
}

export const TIMELINE_BREAKDOWN_LABELS: Record<TimelineBreakdown, string> = {
  total: 'All',
  tagger: 'Assignee',
  class: 'Class',
  workingState: 'Status',
};

export const TIMELINE_VIEW_CONFIG: Record<TimelineView, TimelineViewConfig> = {
  workflow: {
    label: 'Workflow',
    description: 'End-of-day counts of annotated slides moving through Annotating, QA, and Done.',
    metric: 'accumulated',
    defaultBreakdown: 'workingState',
    breakdowns: ['workingState'],
  },
  slideActivity: {
    label: 'Slide activity',
    description: 'Daily count of slides touched by annotation work. Status uses the state after the update.',
    metric: 'slides',
    defaultBreakdown: 'total',
    breakdowns: ['total', 'tagger', 'workingState'],
  },
  annotationContent: {
    label: 'Annotation content',
    description: 'Daily marker counts on annotation-content saves. Status-only revisions are excluded.',
    metric: 'annotations',
    defaultBreakdown: 'total',
    breakdowns: ['total', 'class', 'tagger', 'workingState'],
  },
  revisions: {
    label: 'Revisions',
    description: 'Daily revision events, including annotation edits and status-only moves between workflow states.',
    metric: 'changes',
    defaultBreakdown: 'workingState',
    breakdowns: ['total', 'tagger', 'workingState'],
  },
};

export const getTimelineSeriesLabel = (view: TimelineView) => {
  if (view === 'workflow') return 'Annotated slides';
  if (view === 'slideActivity') return 'Slides with activity';
  if (view === 'annotationContent') return 'Annotation markers';
  return 'Revision events';
};

export const getTimelineYAxisLabel = (view: TimelineView) => {
  if (view === 'workflow') return 'Annotated slides';
  if (view === 'annotationContent') return 'Annotation markers';
  if (view === 'revisions') return 'Revision events';
  return 'Slides';
};

export const getTimelineChartTitle = (view: TimelineView, breakdown: TimelineBreakdown) => {
  if (view === 'workflow') {
    return 'Annotated slides in Annotating -> QA -> Done (end of day)';
  }

  if (view === 'slideActivity') {
    if (breakdown === 'tagger') return 'Slides with annotation activity per day by assignee';
    if (breakdown === 'workingState') return 'Slides with annotation activity per day by resulting status';
    return 'Slides with annotation activity per day';
  }

  if (view === 'annotationContent') {
    if (breakdown === 'class') return 'Annotation marker counts on saved content updates by class';
    if (breakdown === 'tagger') return 'Annotation marker counts on saved content updates by assignee';
    if (breakdown === 'workingState') {
      return 'Annotation marker counts on saved content updates by resulting status';
    }
    return 'Annotation marker counts on saved content updates';
  }

  if (breakdown === 'tagger') return 'Revision events per day by assignee';
  if (breakdown === 'workingState') return 'Revision events per day by resulting status';
  return 'Revision events per day (annotation edits + status changes)';
};
